import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import axios from 'axios';

import {
  AdminRole,
  Affiliation,
  affiliationFactory,
} from '@/types/affiliation';
import { useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { affiliationsApi } from '@/services/api';
import { getFullName } from '@/utils/name';
import i18n from '@/settings/i18n';
import { pages } from '@/settings/pages';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

import { InviteForm } from '../Presentation';

export const InviteFormContainer: React.FC = () => {
  const { apiErrorHandler } = useApiErrorHandler();
  const { flashNotice, flashError } = useFlashMessenger();
  const organizationId = useAppSelector(
    authStore.selectors.organizationIdSelector,
  );
  const gloplaId = useAppSelector(authStore.selectors.gloplaIdSelector);

  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAffiliations = async () => {
      try {
        setIsLoading(true);
        const affiliations =
          await affiliationsApi.getAffiliationsOfOrganization({
            data: {
              organizationId,
            },
          });
        setAffiliations(affiliations);
      } catch (err) {
        apiErrorHandler(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAffiliations();
  }, [apiErrorHandler, organizationId]);

  const inviteAffiliation = useCallback(
    async (email: string, role: AdminRole) => {
      try {
        setIsLoading(true);
        await affiliationsApi.inviteToOrganization({
          data: {
            email,
            role,
            organizationId,
          },
        });

        const admin = (
          affiliations.find((af) => af.admin.email === email) ||
          affiliationFactory()
        ).admin;
        const state = { email: '' };
        if (admin.activatedAt) {
          const message = i18n.t('text.membership.invite.doneWithActivated', {
            name: getFullName(admin.firstName, admin.lastName),
          });
          flashNotice(message);
        } else {
          state.email = email || '';
        }

        navigate(pages.membership.index, {
          state,
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response !== undefined) {
          if (err.response && err.response.status === 304) {
            const message = i18n.t('ui.attention.already.registered', {
              target: i18n.t('models.admin.self'),
            });

            flashNotice(message);
            navigate(pages.membership.index);
          } else {
            if (err.response && err.response.status === 422) {
              flashError(i18n.t('ui.email.error.format'));
            } else {
              apiErrorHandler(err, gloplaId, pages.membership.invite);
            }

            setIsLoading(false);
          }
        }
      }
    },
    [
      affiliations,
      apiErrorHandler,
      flashError,
      flashNotice,
      gloplaId,
      navigate,
      organizationId,
    ],
  );

  const childProps = { isLoading, inviteAffiliation };
  return <InviteForm {...childProps} />;
};
